import React, { useState, useMemo } from 'react'
import { Project } from '@/types'
import ProjectCard from './ProjectCard'
import { motion, AnimatePresence } from 'framer-motion'

type SortOption = 'newest' | 'oldest' | 'alphabetical'

interface ProjectGridProps {
  projects: Project[]
  selectedType: string | null
}

const ProjectGrid: React.FC<ProjectGridProps> = ({ projects, selectedType }) => {
  const [sortBy, setSortBy] = useState<SortOption>('newest')

  // Filter projects based on selected type
  const filteredProjects = useMemo(() => {
    const filtered = selectedType
      ? projects.filter(project => project.projectType === selectedType)
      : projects

    // Sort projects
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
        case 'oldest':
          return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        case 'alphabetical':
          return a.title.localeCompare(b.title)
        default:
          return 0
      }
    })
  }, [projects, selectedType, sortBy])

  return (
    <>
      {/* Sort options */}
      {projects.length > 0 && (
        <div className="flex justify-end mb-6">
          <div className="flex items-center">
            <span className="text-sm mr-2">Sort by:</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as SortOption)}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="alphabetical">Alphabetical</option>
            </select>
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={`${selectedType || 'all'}-${sortBy}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <ProjectCard key={project._id} project={project} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-lg text-gray-600 dark:text-gray-300">
                {projects.length > 0
                  ? 'No projects match the selected filter.'
                  : 'No projects available yet.'}
              </p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </>
  )
}

export default ProjectGrid
